/**
 * Deklarativ reaksiya qoidalari (oksidlanish-qaytarilish, komplekslar, amfoterlik va h.k.).
 *
 * Yozuv: "2i:MnO4 5i:Fe2 8i:H" — koeffitsient + havza + modda:
 *   i — eritmadagi ion, d — molekulyar erigan modda, s — qattiq faza, g — gaz (faqat mahsulot).
 * Suv (H₂O) tenglamalarda ko'rsatilmaydi (erituvchi). Zaryad/atom balansi har qoidada
 * tekshirilgan (izohdagi to'liq tenglama).
 *
 * Tezlik: qadamda erishiladigan ulush 1 − exp(−k·dt), k — 1/s (standart 3 — amalda oniy).
 * MANBA: Glinka; Lurye — sifat tahlili reaksiyalari. Kinetika o'quv namoyishi uchun tanlangan.
 */
import { add, conc, type Vessel } from './vessel';

export type Pool = 'i' | 'd' | 's' | 'g';
export interface Term {
  pool: Pool;
  id: string;
  k: number;
}

export interface Rule {
  name: string;
  R: Term[];
  P: Term[];
  /** 1/s yoki idish holatiga bog'liq funksiya */
  k?: number | ((v: Vessel) => number);
  when?: (v: Vessel) => boolean;
  /** Reaksiya issiqligi, J (1 tenglama birligiga); manfiy — ekzotermik */
  dH?: number;
  event?: string;
}

function terms(spec: string): Term[] {
  return spec
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => {
      const m = /^(\d*(?:\.\d+)?)([idsg]):(\w+)$/.exec(t);
      if (!m) throw new Error(`bad term: ${t}`);
      return { pool: m[2] as Pool, id: m[3] as string, k: m[1] ? Number(m[1]) : 1 };
    });
}

function rule(name: string, R: string, P: string, x: Omit<Rule, 'name' | 'R' | 'P'> = {}): Rule {
  return { name, R: terms(R), P: terms(P), ...x };
}

const c = conc;
const dis = (v: Vessel, id: string) => (v.volumeMl > 0 ? ((v.dissolved[id] ?? 0) / v.volumeMl) * 1000 : 0);
const acidic = (v: Vessel) => c(v, 'H') > 1e-4;
const neutral = (v: Vessel) => c(v, 'H') <= 1e-4 && c(v, 'OH') <= 1e-2;
const basic = (v: Vessel) => c(v, 'OH') > 1e-2;
const hot = (T: number) => (v: Vessel) => v.T >= T;

/** Tartib muhim: kuchli oksidlovchi/qaytaruvchi juftlari avval */
export const RULES: Rule[] = [
  // ── KMnO₄ ──────────────────────────────────────────────────
  // MnO₄⁻ + 5Fe²⁺ + 8H⁺ → Mn²⁺ + 5Fe³⁺ + 4H₂O
  rule('MnO4+Fe2', '1i:MnO4 5i:Fe2 8i:H', '1i:Mn 5i:Fe3'),
  // 2MnO₄⁻ + 5SO₃²⁻ + 6H⁺ → 2Mn²⁺ + 5SO₄²⁻ + 3H₂O
  rule('MnO4+SO3', '2i:MnO4 5i:SO3 6i:H', '2i:Mn 5i:SO4'),
  // 2MnO₄⁻ + 5HSO₃⁻ + H⁺ → 2Mn²⁺ + 5SO₄²⁻ + 3H₂O
  rule('MnO4+HSO3', '2i:MnO4 5i:HSO3 1i:H', '2i:Mn 5i:SO4'),
  // 2MnO₄⁻ + 5SO₂ + 2H₂O → 2Mn²⁺ + 5SO₄²⁻ + 4H⁺
  rule('MnO4+SO2', '2i:MnO4 5d:SO2', '2i:Mn 5i:SO4 4i:H'),
  // 2MnO₄⁻ + 10I⁻ + 16H⁺ → 2Mn²⁺ + 5I₂ + 8H₂O
  rule('MnO4+I', '2i:MnO4 10i:I 16i:H', '2i:Mn 5d:I2'),
  // 2MnO₄⁻ + 10Br⁻ + 16H⁺ → 2Mn²⁺ + 5Br₂ + 8H₂O (sekinroq)
  rule('MnO4+Br', '2i:MnO4 10i:Br 16i:H', '2i:Mn 5d:Br2', { k: 0.3 }),
  // 2MnO₄⁻ + 5H₂O₂ + 6H⁺ → 2Mn²⁺ + 5O₂↑ + 8H₂O
  rule('MnO4+H2O2', '2i:MnO4 5d:H2O2 6i:H', '2i:Mn 5g:O2'),
  // 2MnO₄⁻ + 5H₂S + 6H⁺ → 2Mn²⁺ + 5S↓ + 8H₂O
  rule('MnO4+H2S', '2i:MnO4 5d:H2S 6i:H', '2i:Mn 5s:S'),
  // 2MnO₄⁻ + 10Cl⁻ + 16H⁺ → 2Mn²⁺ + 5Cl₂↑ + 8H₂O (faqat konsentrlangan HCl)
  rule('MnO4+Cl', '2i:MnO4 10i:Cl 16i:H', '2i:Mn 5d:Cl2', { when: (v) => c(v, 'Cl') > 4 && c(v, 'H') > 4, k: 0.5 }),
  // 4MnO₄⁻ + 5C₂H₅OH + 12H⁺ → 4Mn²⁺ + 5CH₃COOH + 11H₂O (sekin)
  rule('MnO4+EtOH', '4i:MnO4 5d:ethanol 12i:H', '4i:Mn 5d:CH3COOH', { k: 0.04 }),
  // Neytral: 2MnO₄⁻ + 3SO₃²⁻ + H₂O → 2MnO₂↓ + 3SO₄²⁻ + 2OH⁻
  rule('MnO4+SO3 neutral', '2i:MnO4 3i:SO3', '2s:MnO2 3i:SO4 2i:OH', { when: neutral }),
  // Ishqoriy: 2MnO₄⁻ + SO₃²⁻ + 2OH⁻ → 2MnO₄²⁻ (yashil) + SO₄²⁻ + H₂O
  rule('MnO4+SO3 basic', '2i:MnO4 1i:SO3 2i:OH', '2i:MnO4g 1i:SO4', { when: basic }),

  // ── K₂Cr₂O₇ ─────────────────────────────────────────────────
  // Cr₂O₇²⁻ + 6Fe²⁺ + 14H⁺ → 2Cr³⁺ + 6Fe³⁺ + 7H₂O
  rule('Cr2O7+Fe2', '1i:Cr2O7 6i:Fe2 14i:H', '2i:Cr3 6i:Fe3'),
  // Cr₂O₇²⁻ + 3SO₃²⁻ + 8H⁺ → 2Cr³⁺ + 3SO₄²⁻ + 4H₂O
  rule('Cr2O7+SO3', '1i:Cr2O7 3i:SO3 8i:H', '2i:Cr3 3i:SO4'),
  // Cr₂O₇²⁻ + 3SO₂ + 2H⁺ → 2Cr³⁺ + 3SO₄²⁻ + H₂O
  rule('Cr2O7+SO2', '1i:Cr2O7 3d:SO2 2i:H', '2i:Cr3 3i:SO4'),
  // Cr₂O₇²⁻ + 6I⁻ + 14H⁺ → 2Cr³⁺ + 3I₂ + 7H₂O
  rule('Cr2O7+I', '1i:Cr2O7 6i:I 14i:H', '2i:Cr3 3d:I2'),
  // Cr₂O₇²⁻ + 3H₂S + 8H⁺ → 2Cr³⁺ + 3S↓ + 7H₂O
  rule('Cr2O7+H2S', '1i:Cr2O7 3d:H2S 8i:H', '2i:Cr3 3s:S'),
  // Cr₂O₇²⁻ + 3H₂O₂ + 8H⁺ → 2Cr³⁺ + 3O₂↑ + 7H₂O
  rule('Cr2O7+H2O2', '1i:Cr2O7 3d:H2O2 8i:H', '2i:Cr3 3g:O2', { k: 0.5 }),
  // 2Cr₂O₇²⁻ + 3C₂H₅OH + 16H⁺ → 4Cr³⁺ + 3CH₃COOH + 11H₂O (alkotest)
  rule('Cr2O7+EtOH', '2i:Cr2O7 3d:ethanol 16i:H', '4i:Cr3 3d:CH3COOH', { k: (v) => (v.T > 330 ? 0.3 : 0.06) }),

  // ── Galogenlar ──────────────────────────────────────────────
  rule('Cl2+I', '1d:Cl2 2i:I', '2i:Cl 1d:I2'),
  rule('Cl2+Br', '1d:Cl2 2i:Br', '2i:Cl 1d:Br2'),
  rule('Br2+I', '1d:Br2 2i:I', '2i:Br 1d:I2'),
  rule('Cl2+Fe2', '1d:Cl2 2i:Fe2', '2i:Cl 2i:Fe3'),
  rule('Br2+Fe2', '1d:Br2 2i:Fe2', '2i:Br 2i:Fe3', { k: 0.6 }),
  // Cl₂ + 2OH⁻ → Cl⁻ + ClO⁻ + H₂O
  rule('Cl2+OH', '1d:Cl2 2i:OH', '1i:Cl 1i:ClO'),
  rule('Br2+OH', '1d:Br2 2i:OH', '1i:Br 1i:BrO'),
  rule('I2+OH', '1d:I2 2i:OH', '1i:I 1i:IO'),
  // I₂ + 2S₂O₃²⁻ → 2I⁻ + S₄O₆²⁻ (yodometriya)
  rule('I2+S2O3', '1d:I2 2i:S2O3', '2i:I 1i:S4O6'),
  // 4Cl₂ + S₂O₃²⁻ + 5H₂O → 8Cl⁻ + 2SO₄²⁻ + 10H⁺ (antixlor)
  rule('Cl2+S2O3', '4d:Cl2 1i:S2O3', '8i:Cl 2i:SO4 10i:H'),
  // X₂ + SO₃²⁻ + H₂O → 2X⁻ + SO₄²⁻ + 2H⁺
  rule('I2+SO3', '1d:I2 1i:SO3', '2i:I 1i:SO4 2i:H'),
  rule('Br2+SO3', '1d:Br2 1i:SO3', '2i:Br 1i:SO4 2i:H'),
  rule('Cl2+SO3', '1d:Cl2 1i:SO3', '2i:Cl 1i:SO4 2i:H'),
  // X₂ + SO₂ + 2H₂O → 2X⁻ + SO₄²⁻ + 4H⁺
  rule('I2+SO2', '1d:I2 1d:SO2', '2i:I 1i:SO4 4i:H'),
  rule('Br2+SO2', '1d:Br2 1d:SO2', '2i:Br 1i:SO4 4i:H'),
  rule('Cl2+SO2', '1d:Cl2 1d:SO2', '2i:Cl 1i:SO4 4i:H'),
  // X₂ + H₂S → 2X⁻ + S↓ + 2H⁺
  rule('Cl2+H2S', '1d:Cl2 1d:H2S', '2i:Cl 1s:S 2i:H'),
  rule('Br2+H2S', '1d:Br2 1d:H2S', '2i:Br 1s:S 2i:H'),
  rule('I2+H2S', '1d:I2 1d:H2S', '2i:I 1s:S 2i:H'),
  rule('I2+S', '1d:I2 1i:S', '2i:I 1s:S'),

  // ── Gipoxlorit, xlorat ──────────────────────────────────────
  // ClO⁻ + Cl⁻ + 2H⁺ → Cl₂↑ + H₂O
  rule('ClO+Cl+H', '1i:ClO 1i:Cl 2i:H', '1d:Cl2'),
  // ClO⁻ + 2I⁻ + H₂O → Cl⁻ + I₂ + 2OH⁻
  rule('ClO+I', '1i:ClO 2i:I', '1i:Cl 1d:I2 2i:OH'),
  rule('ClO+Fe2', '1i:ClO 2i:Fe2 2i:H', '1i:Cl 2i:Fe3'),
  rule('ClO+SO3', '1i:ClO 1i:SO3', '1i:Cl 1i:SO4'),
  // ClO₃⁻ + 5Cl⁻ + 6H⁺ → 3Cl₂↑ + 3H₂O (konsentrlangan HCl)
  rule('ClO3+Cl', '1i:ClO3 5i:Cl 6i:H', '3d:Cl2', { when: (v) => c(v, 'Cl') > 3 && c(v, 'H') > 3, k: 0.3 }),

  // ── Fe³⁺ ────────────────────────────────────────────────────
  // 2Fe³⁺ + 2I⁻ → 2Fe²⁺ + I₂
  rule('Fe3+I', '2i:Fe3 2i:I', '2i:Fe2 1d:I2', { k: 0.8 }),
  // 2Fe³⁺ + SO₃²⁻ + H₂O → 2Fe²⁺ + SO₄²⁻ + 2H⁺
  rule('Fe3+SO3', '2i:Fe3 1i:SO3', '2i:Fe2 1i:SO4 2i:H', { k: 0.8 }),
  rule('Fe3+SO2', '2i:Fe3 1d:SO2', '2i:Fe2 1i:SO4 4i:H', { k: 0.3 }),
  rule('Fe3+H2S', '2i:Fe3 1d:H2S', '2i:Fe2 1s:S 2i:H'),
  rule('Fe3+S', '2i:Fe3 1i:S', '2i:Fe2 1s:S'),
  // 2Fe³⁺ + Cu → 2Fe²⁺ + Cu²⁺ (bosma platani yemirish); 2Fe³⁺ + Fe → 3Fe²⁺
  rule('Fe3+Cu', '2i:Fe3 1s:Cu', '2i:Fe2 1i:Cu', { k: 0.03 }),
  rule('Fe3+Fe', '2i:Fe3 1s:Fe', '3i:Fe2', { k: 0.05 }),

  // ── H₂O₂ ────────────────────────────────────────────────────
  // H₂O₂ + 2I⁻ + 2H⁺ → I₂ + 2H₂O
  rule('H2O2+I', '1d:H2O2 2i:I 2i:H', '1d:I2', { k: 0.15 }),
  rule('H2O2+Fe2', '1d:H2O2 2i:Fe2 2i:H', '2i:Fe3'),
  rule('H2O2+SO3', '1d:H2O2 1i:SO3', '1i:SO4'),
  rule('H2O2+SO2', '1d:H2O2 1d:SO2', '1i:SO4 2i:H'),
  rule('H2O2+H2S', '1d:H2O2 1d:H2S', '1s:S'),

  // ── Kislota-asos: gazlar va molekulyar shakllar ─────────────
  // HCO₃⁻ + OH⁻ → CO₃²⁻ + H₂O
  rule('HCO3+OH', '1i:HCO3 1i:OH', '1i:CO3'),
  rule('HSO3+OH', '1i:HSO3 1i:OH', '1i:SO3'),
  rule('HS+OH', '1i:HS 1i:OH', '1i:S'),
  rule('H2PO4+OH', '1i:H2PO4 2i:OH', '1i:PO4'),
  // SO₂ + 2OH⁻ → SO₃²⁻ + H₂O; H₂S + 2OH⁻ → S²⁻ + 2H₂O
  rule('SO2+OH', '1d:SO2 2i:OH', '1i:SO3'),
  rule('H2S+OH', '1d:H2S 2i:OH', '1i:S'),
  // SO₂ + 2H₂S → 3S↓ + 2H₂O
  rule('SO2+H2S', '1d:SO2 2d:H2S', '3s:S'),
  // H₂S + Cu²⁺/Pb²⁺/2Ag⁺ → MS↓ + 2H⁺ (kislotada ham cho'kadi)
  rule('H2S+Cu', '1d:H2S 1i:Cu', '1s:CuS 2i:H'),
  rule('H2S+Pb', '1d:H2S 1i:Pb', '1s:PbS 2i:H'),
  rule('H2S+Ag', '1d:H2S 2i:Ag', '1s:Ag2S 2i:H'),
  // SO₃²⁻ + 2H⁺ → SO₂ + H₂O; HSO₃⁻ + H⁺ → SO₂ + H₂O
  rule('SO3+H', '1i:SO3 2i:H', '1d:SO2'),
  rule('HSO3+H', '1i:HSO3 1i:H', '1d:SO2'),
  // S²⁻ + 2H⁺ → H₂S; HS⁻ + H⁺ → H₂S
  rule('S+H', '1i:S 2i:H', '1d:H2S'),
  rule('HS+H', '1i:HS 1i:H', '1d:H2S'),
  // S₂O₃²⁻ + 2H⁺ → S↓ + SO₂ + H₂O (sekin: "yo'qolayotgan xoch" tajribasi)
  rule('S2O3+H', '1i:S2O3 2i:H', '1s:S 1d:SO2', { k: (v) => 0.004 * Math.min(5, c(v, 'H')) * Math.exp(0.07 * (v.T - 293.15)) }),
  // SiO₃²⁻/St⁻ + H⁺ — cho'kma (umumiy cho'kma qoidasi); qayta erish:
  rule('HSt+OH', '1s:HSt 1i:OH', '1i:St', { k: 0.3 }),
  rule('H2SiO3+OH', '1s:H2SiO3 2i:OH', '1i:SiO3', { k: 0.2 }),

  // ── Komplekslar va amfoterlik ───────────────────────────────
  // Fe³⁺ + SCN⁻ → [Fe(SCN)]²⁺ (qon-qizil)
  rule('Fe3+SCN', '1i:Fe3 1i:SCN', '1i:FeSCN'),
  rule('FeSCN+OH', '1i:FeSCN 3i:OH', '1s:FeOH3 1i:SCN'),
  // Cu(OH)₂ + 4NH₃ → [Cu(NH₃)₄]²⁺ + 2OH⁻
  rule('CuOH2+NH3', '1s:CuOH2 4d:NH3', '1i:CuNH3 2i:OH', { k: 1.2 }),
  // Ag₂O + 4NH₃ + H₂O → 2[Ag(NH₃)₂]⁺ + 2OH⁻
  rule('Ag2O+NH3', '1s:Ag2O 4d:NH3', '2i:AgNH3 2i:OH', { k: 1.2 }),
  rule('AgCl+NH3', '1s:AgCl 2d:NH3', '1i:AgNH3 1i:Cl', { k: 0.4 }),
  rule('CuNH3+H', '1i:CuNH3 4i:H', '1i:Cu 4i:NH4'),
  rule('AgNH3+H', '1i:AgNH3 2i:H', '1i:Ag 2i:NH4'),
  // Al(OH)₃ + OH⁻ → [Al(OH)₄]⁻; Zn(OH)₂ + 2OH⁻ → [Zn(OH)₄]²⁻
  rule('AlOH3+OH', '1s:AlOH3 1i:OH', '1i:AlOH4', { k: 2 }),
  rule('ZnOH2+OH', '1s:ZnOH2 2i:OH', '1i:ZnOH4', { k: 2 }),
  rule('AlOH4+H', '1i:AlOH4 1i:H', '1s:AlOH3'),
  rule('ZnOH4+H', '1i:ZnOH4 2i:H', '1s:ZnOH2'),
  rule('Al2O3+OH', '1s:Al2O3 2i:OH', '2i:AlOH4', { k: 0.01 }),
  rule('ZnO+OH', '1s:ZnO 2i:OH', '1i:ZnOH4', { k: 0.05 }),
  // Amfoter metallar ishqorda: 2Al + 2OH⁻ + 6H₂O → 2[Al(OH)₄]⁻ + 3H₂↑
  rule('Al+OH', '2s:Al 2i:OH', '2i:AlOH4 3g:H2', { k: (v) => 0.01 * Math.min(5, c(v, 'OH')) * Math.exp(0.04 * (v.T - 293.15)), when: (v) => c(v, 'OH') > 0.3, dH: -830000 }),
  rule('Zn+OH', '1s:Zn 2i:OH', '1i:ZnOH4 1g:H2', { k: (v) => 0.003 * Math.min(5, c(v, 'OH')) * Math.exp(0.04 * (v.T - 293.15)), when: (v) => c(v, 'OH') > 0.5 }),

  // ── Biuret, Feling, Tollens, oqsil, kraxmal ─────────────────
  rule('biuret', '1s:CuOH2', '1i:CuProt 2i:OH', { when: (v) => dis(v, 'protein') > 1e-7 && basic(v), k: 0.6 }),
  rule('glucose+CuOH2', '1s:CuOH2', '1i:CuPoly 2i:OH', { when: (v) => dis(v, 'glucose') > 1e-4 && basic(v) && v.T < 343, k: 0.8 }),
  // 2Cu(OH)₂ + RCHO → Cu₂O↓ + RCOOH + 2H₂O (qizdirilganda)
  rule('Fehling', '2s:CuOH2 1d:glucose', '1s:Cu2O', { when: hot(343), k: 0.12, event: 'chem.redPrecipitate' }),
  rule('Fehling2', '2i:CuPoly 1d:glucose 4i:OH', '1s:Cu2O', { when: hot(343), k: 0.12, event: 'chem.redPrecipitate' }),
  // 2[Ag(NH₃)₂]⁺ + RCHO + 2OH⁻ → 2Ag↓ (ko'zgu) + RCOONH₄ + 3NH₃ + H₂O (soddalashtirilgan)
  rule('Tollens', '2i:AgNH3 1d:glucose', '2s:AgMirror 4d:NH3', { when: hot(318), k: 0.08, event: 'chem.mirror' }),
  rule('protein heat', '1d:protein', '1s:proteinCoag', { when: hot(338), k: 0.3, event: 'chem.coagulated' }),
  rule('protein metal', '1d:protein', '1s:proteinCoag', { when: (v) => c(v, 'Pb') + c(v, 'Ag') + c(v, 'Cu') > 2e-3 || dis(v, 'ethanol') > 4 || c(v, 'H') > 2, k: 0.4, event: 'chem.coagulated' }),
  rule('xanthoprotein', '1s:proteinCoag', '1s:proteinXan', { when: (v) => c(v, 'NO3') > 4 && c(v, 'H') > 4, k: 0.15 }),
  rule('xanthoprotein2', '1d:protein', '1s:proteinXan', { when: (v) => c(v, 'NO3') > 4 && c(v, 'H') > 4, k: 0.3 }),
  // Konsentrlangan H₂SO₄ + saxaroza → C (ko'mirlanish) + H₂O; issiqlik, SO₂/CO₂
  rule('sucrose+H2SO4', '1d:sucrose', '12s:C 0.5g:SO2 0.5g:CO2', { when: (v) => c(v, 'SO4') > 9 && c(v, 'H') > 9, k: 0.15, dH: -400000, event: 'chem.charred' }),
  // Cu + 2H₂SO₄(konts.) → CuSO₄ + SO₂↑ + 2H₂O (qizdirilganda)
  rule('Cu+H2SO4 conc', '1s:Cu 4i:H 1i:SO4', '1i:Cu 1d:SO2', { when: (v) => c(v, 'SO4') > 8 && v.T > 343, k: 0.03 }),
  // MnO₂ + 4HCl(konts.) → MnCl₂ + Cl₂↑ + 2H₂O (qizdirilganda)
  rule('MnO2+HCl', '1s:MnO2 4i:H 2i:Cl', '1i:Mn 1d:Cl2', { when: (v) => c(v, 'Cl') > 4 && v.T > 323, k: 0.05 }),

  // ── Fuksin: SO₂ bilan qaytar rangsizlanish, Cl₂/ClO⁻ bilan qaytmas ──
  rule('fuchsin+SO2', '1d:fuchsin 1d:SO2', '1d:fuchsinSO2', { when: (v) => v.T < 340, k: 1 }),
  rule('fuchsinSO2 heat', '1d:fuchsinSO2', '1d:fuchsin 1g:SO2', { when: hot(343), k: 0.2 }),
  rule('fuchsin+Cl2', '1d:fuchsin 1d:Cl2', '2i:Cl 2i:H', { k: 0.5 }),
  rule('fuchsin+ClO', '1d:fuchsin 1i:ClO', '1i:Cl', { k: 0.5 }),

  // ── Qattiq suv qaynatilganda ────────────────────────────────
  // Ca(HCO₃)₂ → CaCO₃↓ + CO₂↑ + H₂O
  rule('CaHCO3 boil', '1i:Ca 2i:HCO3', '1s:CaCO3 1g:CO2', { when: hot(353), k: 0.06 }),
  rule('MgHCO3 boil', '1i:Mg 2i:HCO3', '1s:MgCO3 1g:CO2', { when: hot(353), k: 0.04 }),
  rule('BaHCO3 boil', '1i:Ba 2i:HCO3', '1s:BaCO3 1g:CO2', { when: hot(353), k: 0.06 }),

  // ── Suv bilan reaksiyaga kirishuvchi qattiq moddalar ────────
  // 2Na₂O₂ + 2H₂O → 4NaOH + O₂↑
  rule('Na2O2+water', '2s:Na2O2', '4i:Na 4i:OH 1g:O2', { k: 0.4, dH: -250000 }),
  // CaC₂ + 2H₂O → Ca(OH)₂ + C₂H₂↑
  rule('CaC2+water', '1s:CaC2', '1i:Ca 2i:OH 1g:C2H2', { k: 0.15, dH: -128000 }),

  // ── Birgalikdagi (qaytmas) gidroliz: kuchsiz asos kationi + kuchsiz kislota anioni ──
  // 2Al³⁺ + 3CO₃²⁻ + 3H₂O → 2Al(OH)₃↓ + 3CO₂↑ (Al₂(CO₃)₃ suvda mavjud emas)
  rule('Al+CO3 hydrolysis', '2i:Al 3i:CO3', '2s:AlOH3 3g:CO2', { k: 2 }),
  rule('Fe3+CO3 hydrolysis', '2i:Fe3 3i:CO3', '2s:FeOH3 3g:CO2', { k: 2 }),
  rule('Cr3+CO3 hydrolysis', '2i:Cr3 3i:CO3', '2s:CrOH3 3g:CO2', { k: 2 }),
  // Al³⁺ + 3HCO₃⁻ → Al(OH)₃↓ + 3CO₂↑ (ko'pikli o't o'chirgich)
  rule('Al+HCO3 hydrolysis', '1i:Al 3i:HCO3', '1s:AlOH3 3g:CO2', { k: 2 }),
  rule('Fe3+HCO3 hydrolysis', '1i:Fe3 3i:HCO3', '1s:FeOH3 3g:CO2', { k: 2 }),
  // 2Al³⁺ + 3S²⁻ + 6H₂O → 2Al(OH)₃↓ + 3H₂S↑
  rule('Al+S hydrolysis', '2i:Al 3i:S', '2s:AlOH3 3d:H2S', { k: 2 }),
  rule('Cr3+S hydrolysis', '2i:Cr3 3i:S', '2s:CrOH3 3d:H2S', { k: 2 }),
  rule('Al+HS hydrolysis', '1i:Al 3i:HS', '1s:AlOH3 3d:H2S', { k: 2 }),
  rule('Cr3+HS hydrolysis', '1i:Cr3 3i:HS', '1s:CrOH3 3d:H2S', { k: 2 }),
  // 2Al³⁺ + 3SiO₃²⁻ + 6H₂O → 2Al(OH)₃↓ + 3H₂SiO₃↓
  rule('Al+SiO3 hydrolysis', '2i:Al 3i:SiO3', '2s:AlOH3 3s:H2SiO3', { k: 2 }),
  // Gidroliz tufayli kislotali NH₄Cl eritmasida magniy eriydi: Mg + 2NH₄⁺ → Mg²⁺ + 2NH₃ + H₂↑
  // ── Qizdirishda gidroliz kuchayadi ──
  // Fe³⁺ + 3H₂O → Fe(OH)₃ (zol, qizil-qo'ng'ir) + 3H⁺ (suyultirilgan FeCl₃ qaynatilganda)
  rule('Fe3 hot hydrolysis', '1i:Fe3', '1s:FeOH3 3i:H', { when: (v) => v.T >= 353 && c(v, 'Fe3') < 0.15 && c(v, 'H') < 0.03, k: 0.08 }),
  // 2HCO₃⁻ → CO₃²⁻ + CO₂↑ + H₂O (NaHCO₃ eritmasi qaynatilganda soda hosil bo'ladi, muhit ishqoriylashadi)
  rule('HCO3 boil', '2i:HCO3', '1i:CO3 1g:CO2', { when: hot(358), k: 0.04 }),
  // Mg(OH)₂ ammoniy tuzlarida eriydi: Mg(OH)₂ + 2NH₄⁺ → Mg²⁺ + 2NH₃ + 2H₂O
  rule('MgOH2+NH4', '1s:MgOH2 2i:NH4', '1i:Mg 2d:NH3', { k: 0.6 }),
  rule('Mg+NH4', '1s:Mg 2i:NH4', '1i:Mg 2d:NH3 1g:H2', { k: (v) => 0.004 * Math.min(3, c(v, 'NH4')) * Math.exp(0.05 * (v.T - 293.15)), dH: -300000 }),
];

/** Kislotali muhit sharti (tashqi foydalanish uchun) */
export const env = { acidic, neutral, basic };

export interface RuleCtx {
  evolve: (gasId: string, n: number) => void;
  heat: (J: number) => void;
  event: (code: string, params?: Record<string, string | number>) => void;
}

function poolOf(v: Vessel, p: Pool): Record<string, number> {
  return p === 'i' ? v.ions : p === 'd' ? v.dissolved : v.solids;
}

/** Barcha qoidalarni bir marta qo'llash (suyuq faza bo'lsa) */
export function applyRules(v: Vessel, dt: number, ctx: RuleCtx, rules: Rule[] = RULES): void {
  if (v.volumeMl <= 0) return;
  for (const r of rules) {
    let xi = Infinity;
    for (const t of r.R) xi = Math.min(xi, (poolOf(v, t.pool)[t.id] ?? 0) / t.k);
    if (!(xi > 1e-12)) continue;
    if (r.when && !r.when(v)) continue;
    const k = typeof r.k === 'function' ? r.k(v) : (r.k ?? 3);
    const x = xi * (1 - Math.exp(-k * dt));
    if (x <= 1e-14) continue;
    for (const t of r.R) add(poolOf(v, t.pool), t.id, -x * t.k);
    for (const t of r.P) {
      if (t.pool === 'g') ctx.evolve(t.id, x * t.k);
      else add(poolOf(v, t.pool), t.id, x * t.k);
    }
    if (r.dH) ctx.heat(-x * r.dH);
    if (r.event) ctx.event(r.event);
  }
}
